'use client'

import { useState } from 'react'
import { cn } from '../../utils/cn'
import { ChevronDown } from 'lucide-react'
import { Section } from '../layout/Section'
import { Container } from '../layout/Container'
import { FadeIn } from '../motion/FadeIn'

interface FAQItem {
  question: string
  answer: string
}

interface FAQProps {
  id?: string
  sectionNumber?: string
  heading: string
  subheading?: string
  items: FAQItem[]
  columns?: 1 | 2
}

function FAQItemComponent({
  question,
  answer,
  isOpen,
  onToggle
}: {
  question: string
  answer: string
  isOpen: boolean
  onToggle: () => void
}) {
  return (
    <div className="faq-item">
      <button
        type="button"
        onClick={onToggle}
        className="faq-question"
        aria-expanded={isOpen}
      >
        <span className="pr-4">{question}</span>
        <ChevronDown
          className={cn(
            'h-5 w-5 flex-shrink-0 text-[var(--landing-text-muted)] transition-all duration-300',
            isOpen && 'rotate-180 text-[var(--accent)]'
          )}
        />
      </button>
      <div
        className={cn(
          'grid transition-all duration-300 ease-out',
          isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
        )}
      >
        <div className="overflow-hidden">
          <div className="faq-answer">
            {answer}
          </div>
        </div>
      </div>
    </div>
  )
}

export function FAQ({
  id,
  sectionNumber,
  heading,
  subheading,
  items,
  columns = 1
}: FAQProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(0)

  const handleToggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index)
  }

  // Split items for 2-column layout
  const midpoint = Math.ceil(items.length / 2)
  const leftItems = items.slice(0, midpoint)
  const rightItems = items.slice(midpoint)

  return (
    <Section id={id} number={sectionNumber}>
      <Container size="md">
        {/* Section Header */}
        <div className="text-center mb-12 max-w-2xl mx-auto">
          <FadeIn>
            <h2 className="headline-lg mb-4">{heading}</h2>
          </FadeIn>
          {subheading && (
            <FadeIn delay={0.1}>
              <p className="body-lg">{subheading}</p>
            </FadeIn>
          )}
        </div>

        {/* FAQ Items */}
        <FadeIn delay={0.2}>
          {columns === 1 ? (
            <div className="max-w-2xl mx-auto faq-container">
              {items.map((item, index) => (
                <FAQItemComponent
                  key={index}
                  question={item.question}
                  answer={item.answer}
                  isOpen={openIndex === index}
                  onToggle={() => handleToggle(index)}
                />
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-6">
              <div className="faq-container">
                {leftItems.map((item, index) => (
                  <FAQItemComponent
                    key={index}
                    question={item.question}
                    answer={item.answer}
                    isOpen={openIndex === index}
                    onToggle={() => handleToggle(index)}
                  />
                ))}
              </div>
              <div className="faq-container">
                {rightItems.map((item, index) => (
                  <FAQItemComponent
                    key={index + midpoint}
                    question={item.question}
                    answer={item.answer}
                    isOpen={openIndex === index + midpoint}
                    onToggle={() => handleToggle(index + midpoint)}
                  />
                ))}
              </div>
            </div>
          )}
        </FadeIn>
      </Container>
    </Section>
  )
}
